/**
 * `/email-templates` — the page's wire shapes and the pure decisions it makes
 * about them. Dependency-light (no React, no supabase) so the rules are
 * unit-tested directly.
 */

import { NEWSLETTER_TEST_SEND_BELOW } from "@layer/canonical-data/email-campaigns";
import {
  FLOW_OPT_OUT_URL_TOKEN,
  checkMarketingEmailTemplate,
} from "@layer/canonical-data/email-unsubscribe";
import {
  emailTemplateNameProblems,
  memberNameTemplateProblems,
} from "@layer/canonical-data/member-first-name";

/** One `GET /api/email-templates` entry (`apps/dashboard/src/email-inventory.ts`). */
export interface EmailEntry {
  readonly kind: string;
  readonly label: string;
  readonly category: "transactional" | "marketing" | "nurture" | "auth";
  readonly editable: boolean;
  readonly previewable: boolean;
  /** The studio has a saved edit of it. Absent when the saved edits could not be read. */
  readonly edited?: boolean;
  readonly flow?: string;
  readonly step?: number;
  readonly layout?: "design" | "generic" | "plain";
  readonly in_flow?: boolean;
}

/** `GET /api/email-templates/:kind/preview` (`apps/dashboard/src/email-preview.ts`). */
export interface EmailPreviewResponse {
  readonly kind: string;
  /** The email's HTML; `null` when it goes out as plain text. */
  readonly html: string | null;
  readonly text: string | null;
  /** The subject line it goes out under; `null` when the sending step is not known. */
  readonly subject: string | null;
  /** What is shown — which is what a send would put on the wire. */
  readonly source: "built_in" | "saved_edit" | "shared_layout" | "plain_text";
  /** The studio has a saved edit of this email, whether or not it is the one shown. */
  readonly edited: boolean;
  /** Placeholders a send cannot fill; `unfilled_in` says where they are. */
  readonly unfilled: ReadonlyArray<string>;
  readonly unfilled_in: "saved_edit" | "step" | null;
  /**
   * Why the saved edit is not the email shown, besides placeholders: a
   * marketing email's edit that lost its unsubscribe link, or a marketing
   * step's edit with no plain-text version (2026-10-23). Empty when there is
   * no saved edit, or it is the one shown.
   */
  readonly saved_edit_problems: ReadonlyArray<string>;
  /**
   * The flow has no email step at this position, so nothing sends what is
   * shown: a design (or its saved edit) left behind by a flow edit.
   */
  readonly step_missing: boolean;
}

/** The document the preview frame shows: the email's HTML, or its plain text. */
export function previewDocument(preview: EmailPreviewResponse): string {
  return preview.html ?? textToHtmlDocument(preview.text ?? "");
}

/** The subject line to show above a preview, or `undefined` when there is none. */
export function previewSubject(preview: EmailPreviewResponse): string | undefined {
  const subject = (preview.subject ?? "").trim();
  return subject.length > 0 ? subject : undefined;
}

/**
 * What the popup over a row of the Emails page is headed by: the row's label.
 * One kind of row has no name of its own: a marketing step without a design is
 * labelled with its subject AS WRITTEN, placeholders included ("This month's
 * read: {{ article.source }} on EMS"). Over the email itself that reads as a
 * fault, so its popup is headed by the subject as the server filled it. When
 * the server gives none (the step has no subject) the label stands.
 */
export function previewHeading(
  entry: Pick<EmailEntry, "label" | "layout">,
  preview: EmailPreviewResponse,
): string {
  const labelledBySubject = entry.layout === "generic" || entry.layout === "plain";
  return labelledBySubject ? (previewSubject(preview) ?? entry.label) : entry.label;
}

function listed(tokens: ReadonlyArray<string>): string {
  return tokens.join(", ");
}

/**
 * Why the studio's saved version is not what a send uses, as one clause: the
 * placeholders it uses that this email cannot fill, and what it lost (the
 * unsubscribe link a marketing email keeps, a plain-text version). `null`
 * when nothing stands in its way.
 */
function whySavedVersionUnused(preview: EmailPreviewResponse): string | null {
  const reasons: string[] = [];
  if (preview.unfilled_in === "saved_edit") {
    reasons.push(`it uses ${listed(preview.unfilled)}, which this email cannot fill`);
  }
  reasons.push(...preview.saved_edit_problems);
  return reasons.length === 0 ? null : reasons.join("; ");
}

/**
 * What the popup says it is showing. The preview is the email a send would put
 * on the wire, so every sentence here is about what members get — above all
 * when that is NOT the version the studio saved, or nothing at all.
 *
 * `unsavedFlowChanges` — opened from the flow editor with edits not yet
 * saved: the server only knows the flow as it runs.
 * `designLeftBehind` — opened from a design's own row whose step the flow no
 * longer has (`in_flow: false`): the key is a POSITION, so what comes back is
 * whatever that position sends now.
 */
export function previewSubtitle(
  preview: EmailPreviewResponse,
  opts: {
    readonly unsavedFlowChanges?: boolean;
    readonly designLeftBehind?: boolean;
  } = {},
): string {
  const tail =
    opts.unsavedFlowChanges === true
      ? " This is the flow as it runs now, not your unsaved changes — save to see them."
      : "";
  if (preview.step_missing) {
    const shown =
      preview.source === "saved_edit" ? "your saved version" : "the design";
    const why = whySavedVersionUnused(preview);
    const unusable =
      why === null ? "" : ` Your saved version would not be used either: ${why}.`;
    return `Nothing sends this email: the flow has no email step at this position. Shown is ${shown}, with sample values.${unusable}${tail}`;
  }
  const lead =
    opts.designLeftBehind === true
      ? "This design is not in use — shown is the step now at its position. "
      : "";
  if (preview.unfilled_in === "step") {
    return `${lead}This email cannot be sent as written: ${listed(preview.unfilled)} ${
      preview.unfilled.length === 1 ? "has" : "have"
    } no value in this flow, so the step fails instead of sending. Shown with ${
      preview.unfilled.length === 1 ? "it" : "them"
    } left in.${tail}`;
  }
  const why = whySavedVersionUnused(preview);
  if (why !== null) {
    const sent =
      preview.source === "shared_layout"
        ? "the step's own text in the shared layout"
        : "the built-in version";
    return `${lead}Your saved version is not being sent: ${why}. Members get ${sent}, shown here with sample values.${tail}`;
  }
  switch (preview.source) {
    case "saved_edit":
      return `${lead}Your saved version, as members get it. Sample values filled in.${tail}`;
    case "plain_text":
      return preview.edited
        ? `${lead}This step is sent as plain text, so your saved version is not used. Sample values filled in.${tail}`
        : `${lead}Sent as plain text, with no layout. Sample values filled in.${tail}`;
    case "shared_layout":
      return `${lead}The step's own text in the shared layout, as members get it. Sample values filled in.${tail}`;
    case "built_in":
      return `${lead}The built-in version, as members get it. Sample values filled in.${tail}`;
  }
}

/**
 * What to say when a preview could not be had. The preview refuses where a
 * guess would be shown as what members get, so each refusal is worth a
 * sentence; anything else keeps its status and code.
 */
export function previewFailureText(
  failure: { readonly status: number; readonly code: string | null },
  opts: { readonly unsavedFlowChanges?: boolean } = {},
): string {
  switch (failure.code) {
    case "email_template_not_found":
      return opts.unsavedFlowChanges === true
        ? "The flow as it runs now has no email at this position. Save the flow, then View shows this step."
        : "There is no email at this position in the flow as it runs now.";
    case "flow_definitions_unavailable":
      return "This studio's flows could not be read just now, so the email cannot be shown as it is sent. Try again in a moment.";
    case "email_inventory_unbound":
      return "Saved email edits cannot be read on this server, so the email cannot be shown as it is sent.";
    default:
      return `preview_failed:${String(failure.status)}${
        failure.code !== null ? `:${failure.code}` : ""
      }`;
  }
}

/** How the flow editor previews and edits one email step. */
export interface EmailStepPreview {
  /** The key `GET /api/email-templates/:kind/preview` shows the step under. */
  readonly kind: string;
  /** Whether `/email-templates/:kind/edit` has an email to start from. */
  readonly htmlEditable: boolean;
  /**
   * `own` — a template or bespoke design of its own; `generic` — the shared
   * layout around the step's subject and text; `plain` — no layout at all.
   */
  readonly layout: "own" | "generic" | "plain";
}

/**
 * What previews the email step at `index` of a flow, or `null` when nothing
 * does.
 *
 * A marketing flow (`engine: "drip"`) previews every email step under its
 * POSITION key `flow:<flow>:step<N>` — the preview shows the bespoke design,
 * the shared layout or the plain text, whichever a send uses. Only a
 * `template: nurture` step at a position with a design in code
 * (`designedSteps`) can open the HTML editor: it starts from the design, and
 * a step without one gives it nothing to load.
 *
 * Any other flow composes HTML only for a step that names a `template:`, and
 * that template is the email's kind; a step without one sends its text alone.
 */
export function emailStepPreview(args: {
  readonly flowName: string;
  readonly engine: "drip" | "immediate" | null;
  readonly designedSteps: ReadonlyArray<number>;
  readonly step: { readonly action: string; readonly template?: string };
  readonly index: number;
}): EmailStepPreview | null {
  const { flowName, engine, designedSteps, step, index } = args;
  if (step.action !== "send_email") return null;
  const position = index + 1;
  const positionKey = `flow:${flowName}:step${String(position)}`;
  const template =
    step.template !== undefined && step.template.length > 0
      ? step.template
      : null;
  if (engine === "drip") {
    if (template !== "nurture") {
      return { kind: positionKey, htmlEditable: false, layout: "plain" };
    }
    const designed = designedSteps.includes(position);
    return {
      kind: positionKey,
      htmlEditable: designed,
      layout: designed ? "own" : "generic",
    };
  }
  if (template === null) return null;
  return {
    kind: template === "nurture" ? positionKey : template,
    htmlEditable: true,
    layout: "own",
  };
}

/** Anything in double braces — a placeholder the list lacks is still one. */
const ANY_TOKEN = /\{\{([^}]*)\}\}/g;

function escapeHtmlValue(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Fill a draft email's placeholders for the editor's Preview.
 *
 * `sample` is the server's list of every placeholder a saved edit of this
 * email may use, with a sample value each (`GET …/:kind/seed`). A value goes
 * into the HTML escaped, as a send escapes it. A placeholder the list does not
 * have is left as written and returned in `unfilled` — a send cannot fill it
 * either, and then sends the built-in email instead of the saved one.
 */
export function fillSampleTokens(
  html: string,
  sample: Readonly<Record<string, string>>,
): { readonly html: string; readonly unfilled: ReadonlyArray<string> } {
  const unfilled: string[] = [];
  const filled = html.replace(ANY_TOKEN, (token, inner: string) => {
    const key = inner.trim();
    const value = Object.hasOwn(sample, key) ? sample[key] : undefined;
    if (value === undefined) {
      if (!unfilled.includes(token)) unfilled.push(token);
      return token;
    }
    return escapeHtmlValue(value);
  });
  return { html: filled, unfilled };
}

/**
 * The editor Preview's subtitle: what it shows, what a send would not fill,
 * and why Save would refuse it. A placeholder the save refuses on its own —
 * the stored name, which the editor does not offer (2026-10-23,
 * saved-edit-name-rules) — is left unfilled too, but a send could fill it:
 * Save's refusal is the reason, and the only one given for it.
 */
export function draftPreviewSubtitle(
  unfilled: ReadonlyArray<string>,
  saveProblems: ReadonlyArray<string> = [],
): string {
  const parts = ["Your edited HTML with sample values filled in."];
  const notFilled = unfilled.filter((token) => memberNameTemplateProblems(token).length === 0);
  if (notFilled.length > 0) {
    parts.push(
      `Not filled: ${listed(notFilled)} — a send cannot fill ${
        notFilled.length === 1 ? "it" : "them"
      } either, so members would get the built-in version instead of this one.`,
    );
  }
  if (saveProblems.length > 0) {
    parts.push(`Save will refuse it: ${saveProblems.join("; ")}.`);
  }
  return parts.join(" ");
}

/**
 * The flow editor's label over an email step's text. A step that honours
 * opt-outs sends a marketing email, whose text must carry the member's
 * unsubscribe address: `edit_marketing_flow` refuses a Save without it
 * (`flow_unsubscribe_link_dropped`, 2026-10-23), so the box says so first.
 */
export function flowStepTextBodyLabel(step: { readonly opt_out_check?: string }): string {
  return step.opt_out_check !== undefined
    ? "Text body — keep {{ flow.opt_out_url }} in it: it is the member's unsubscribe link"
    : "Text body";
}

/**
 * The text a step added in the flow editor starts with. A marketing email
 * (one that takes an opt-out key) starts with the unsubscribe line the shipped
 * steps close on, so the new step is not the one a Save refuses for dropping
 * the link — the same reason it takes the last step's opt-out key.
 */
export function newFlowStepTextBody(step: {
  readonly action: string;
  readonly opt_out_check?: string;
}): string {
  return step.action === "send_email" && step.opt_out_check !== undefined
    ? `\n\nDon't want these emails? ${FLOW_OPT_OUT_URL_TOKEN}`
    : "";
}

/** `GET /api/email-templates/:kind/seed`: the starting HTML and its sample values. */
export interface EditorSeed {
  readonly html_body: string;
  readonly text_body: string;
  /** Every placeholder a saved edit of this email may use → a sample value. */
  readonly sample: Readonly<Record<string, string>>;
  /** A marketing email: a saved edit must keep its unsubscribe link (2026-10-23). */
  readonly marketing: boolean;
  /** A marketing step: a saved edit must have a plain-text version, which is what is sent. */
  readonly text_required: boolean;
}

/**
 * Why `edit_email_template` would refuse to save this draft — the checks it
 * runs, in its order, on the fields the page sends (an empty plain-text box
 * is no plain text): a marketing email's unsubscribe link, then, for every
 * email, the greeting rule (2026-10-23, saved-edit-name-rules). Empty when
 * Save would take it.
 */
export function draftSaveProblems(
  seed: Pick<EditorSeed, "marketing" | "text_required">,
  draft: { readonly html: string; readonly text: string },
): ReadonlyArray<string> {
  const text = draft.text.length > 0 ? draft.text : null;
  const unsubscribe = seed.marketing
    ? checkMarketingEmailTemplate({ html: draft.html, text, textRequired: seed.text_required })
    : [];
  return [...unsubscribe, ...emailTemplateNameProblems({ html: draft.html, text })];
}

/** `read_email_template` as the editor gets it back: an answer, or a refusal. */
export type SavedEditRead =
  | {
      readonly ok: true;
      readonly response: {
        readonly override: {
          readonly html_body: string;
          readonly text_body: string | null;
        } | null;
      };
    }
  | { readonly ok: false; readonly code: string; readonly message: string };

/** What the email editor opens on, or why it stays closed. */
export type EditorLoad =
  | {
      readonly ok: true;
      readonly html: string;
      readonly text: string;
      readonly hasSavedEdit: boolean;
    }
  | { readonly ok: false; readonly message: string };

/**
 * Decide what the email editor opens on from its two reads.
 *
 * A saved edit that could not be READ is not "no saved edit". Opening on the
 * starting HTML there says "No override yet" over an email that may have one,
 * and a Save then replaces a version nobody was shown. So a refused read opens
 * nothing and says why.
 */
export function editorLoad(seed: EditorSeed, read: SavedEditRead): EditorLoad {
  if (!read.ok) {
    return {
      ok: false,
      message: `Could not check whether this email has a saved version, so the editor stays closed: a Save from here could replace a version you have not seen. (${read.code}: ${read.message})`,
    };
  }
  const saved = read.response.override;
  return {
    ok: true,
    html: saved?.html_body ?? seed.html_body,
    text: saved?.text_body ?? seed.text_body,
    hasSavedEdit: saved !== null,
  };
}

/** What to say when the editor's starting HTML could not be had. */
export function editorLoadFailureText(failure: {
  readonly status: number;
  readonly code: string | null;
}): string {
  if (failure.code === "email_template_not_editable") {
    return "This email has no HTML editor.";
  }
  return `load_failed:${String(failure.status)}${
    failure.code !== null ? `:${failure.code}` : ""
  }`;
}

/** The fields of a `list_marketing_flows` flow this page reads. */
export interface ListedFlow {
  readonly name: string;
  readonly enabled: boolean;
  readonly enabled_source: "override" | "default";
}

/** One `GET /api/email-templates/live` staff-alert row. */
export interface StaffAlertCount {
  readonly type: string;
  readonly by_email: number;
  readonly admin_only: boolean;
}

/** One `GET /api/email-templates/live` newsletter row. */
export interface Newsletter {
  readonly broadcast_id: string;
  readonly subject: string;
  readonly first_seen_at: string;
  readonly delivered: number;
  readonly bounced: number;
  readonly sample_message_id: string;
}

export interface MarketingFlowGroup {
  readonly flow: string;
  readonly emails: ReadonlyArray<EmailEntry>;
}

/**
 * The marketing emails, one group per flow, in the order the server sent them
 * (flow by flow, step by step). An entry without a flow cannot be placed and
 * is left out — the server sets one on every marketing entry.
 */
export function groupMarketingByFlow(
  entries: ReadonlyArray<EmailEntry>,
): ReadonlyArray<MarketingFlowGroup> {
  const groups: Array<{ flow: string; emails: EmailEntry[] }> = [];
  for (const entry of entries) {
    if (entry.category !== "marketing" || entry.flow === undefined) continue;
    const last = groups[groups.length - 1];
    if (last !== undefined && last.flow === entry.flow) {
      last.emails.push(entry);
    } else {
      groups.push({ flow: entry.flow, emails: [entry] });
    }
  }
  return groups;
}

export interface FlowSwitch {
  /** `null` — the flow list did not load, or does not carry this flow. */
  readonly on: boolean | null;
  /**
   * Nobody has switched the flow, so `on` is its shipped default — which is
   * not what every gate does with a flow nobody switched (`/flows` shows the
   * same "not set" beside its checkbox, `resolveFlowToggleState`).
   */
  readonly notSet: boolean;
}

/** What to say about the switch that decides whether a flow's emails go out. */
export function flowSwitch(
  flowName: string,
  flows: ReadonlyArray<ListedFlow> | null,
): FlowSwitch {
  const flow = flows?.find((f) => f.name === flowName);
  if (flow === undefined) return { on: null, notSet: false };
  return { on: flow.enabled, notSet: flow.enabled_source === "default" };
}

export interface StaffAlertRow {
  readonly type: string;
  readonly label: string;
  readonly hint: string;
  readonly byEmail: number;
}

/**
 * The staff-alert rows: the server's alert types (it decides WHICH types can
 * arrive by email, and counts), named from the shared grid vocabulary. A type
 * the vocabulary does not know yet is still listed, under its raw name — the
 * page must never hide an email because a label is missing. The vocabulary's
 * labels already say "(admin-only)" where it applies, so only that raw-name
 * fallback takes it from the server's flag.
 */
export function staffAlertRows(
  counts: ReadonlyArray<StaffAlertCount>,
  vocabulary: ReadonlyArray<readonly [string, string, string]>,
): ReadonlyArray<StaffAlertRow> {
  // Follow the vocabulary's order (the order `/config` lists them in), then
  // anything it does not name.
  const position = (type: string): number => {
    const i = vocabulary.findIndex(([t]) => t === type);
    return i === -1 ? vocabulary.length : i;
  };
  return [...counts]
    .sort((a, b) => position(a.type) - position(b.type))
    .map((row) => {
      const named = vocabulary.find(([t]) => t === row.type);
      return {
        type: row.type,
        label:
          named?.[1] ??
          (row.admin_only ? `${row.type} (admin-only)` : row.type),
        hint: named?.[2] ?? "",
        byEmail: row.by_email,
      };
    });
}

/** "Nobody" / "1 person" / "3 people". */
export function peopleCount(n: number): string {
  if (n <= 0) return "Nobody";
  return n === 1 ? "1 person" : `${String(n)} people`;
}

/**
 * A broadcast that reached fewer people than this is a test send to
 * colleagues, not a newsletter to the list — the one threshold `/campaigns`
 * and the newsletter tracker use too (`@layer/canonical-data/email-campaigns`).
 */
export { NEWSLETTER_TEST_SEND_BELOW };

/** Split the broadcasts into newsletters to the list and test sends. */
export function splitNewsletters(rows: ReadonlyArray<Newsletter>): {
  readonly sent: ReadonlyArray<Newsletter>;
  readonly tests: ReadonlyArray<Newsletter>;
} {
  return {
    sent: rows.filter((r) => r.delivered >= NEWSLETTER_TEST_SEND_BELOW),
    tests: rows.filter((r) => r.delivered < NEWSLETTER_TEST_SEND_BELOW),
  };
}

/** `<a …>` / `<area …>` opening tags, quote-aware so a `>` in a value is not the end. */
const LINK_TAG = /<(?:a|area)\b(?:[^>"']|"[^"]*"|'[^']*')*>/gi;
const HREF_ATTR = /\s+href\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'>]+)/gi;

/**
 * Make every link in a sent email inert.
 *
 * A newsletter is shown from ONE recipient's delivered copy, and that copy's
 * unsubscribe link is theirs: following it from the dashboard would
 * unsubscribe a real person. Rather than try to recognise which links are
 * personal (a tracking redirect can wrap any of them), none of them work. The
 * `href`s are removed, and a style rule stops pointer activation of anything
 * the removal missed.
 */
export function disableLinks(html: string): string {
  const stripped = html.replace(LINK_TAG, (tag) => tag.replace(HREF_ATTR, ""));
  const guard =
    "<style>a,area{pointer-events:none!important;cursor:default!important}</style>";
  const bodyEnd = stripped.search(/<\/body\s*>/i);
  return bodyEnd === -1
    ? stripped + guard
    : stripped.slice(0, bodyEnd) + guard + stripped.slice(bodyEnd);
}

/** A plain-text email, as a document the preview frame can show. */
export function textToHtmlDocument(text: string): string {
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return `<!doctype html><html><body style="margin:0;padding:16px;font:14px/1.5 -apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif"><pre style="white-space:pre-wrap;word-wrap:break-word;margin:0">${escaped}</pre></body></html>`;
}
